/**
 * Date arithmetic for policy periods.
 *
 * Everything here operates on ISO date strings (`YYYY-MM-DD`) and constructs Dates in
 * UTC. Policy periods are calendar facts, not instants — running them through a local
 * timezone is how a renewal silently lands a day early for half the year.
 */

import { FREQUENCY_MONTHS, type RenewalFrequency } from './enums.js';

export type IsoDate = string;

/** Days before `end_date` at which a policy enters the renewal window (spec §6.3). */
export const DEFAULT_RENEWAL_LEAD_DAYS = 30;

export function toIsoDate(date: Date): IsoDate {
  return date.toISOString().slice(0, 10);
}

export function parseIsoDate(value: IsoDate): Date {
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) throw new Error(`Invalid ISO date: "${value}"`);
  return new Date(Date.UTC(y, m - 1, d));
}

/**
 * The office's clock.
 *
 * Not the machine's: the book is kept in Greece, and every date in it — when a policy
 * lapses, which renewal column a card lands in, what day a payment was taken — is a fact
 * about a Greek calendar day. Pinned to the zone rather than left to `toLocaleDateString`
 * so a laptop set to UTC, a container that is always UTC, and the office PC all answer the
 * same question the same way.
 */
export const OFFICE_TIME_ZONE = 'Europe/Athens';

const OFFICE_DAY = new Intl.DateTimeFormat('en-CA', {
  timeZone: OFFICE_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * Today, as the office would write it.
 *
 * This was `new Date().toISOString().slice(0, 10)`, which is today in UTC — and Greece runs
 * two hours ahead in winter and three in summer. So between midnight and 02:00 (03:00 in
 * summer) the whole app still believed it was yesterday: the renewals board kept a policy in
 * «0–6 ημέρες» after it had expired, and «Ημ. πληρωμής» opened on the previous day. Assembled
 * from parts rather than from the formatted string, so no locale or ICU build can slip a
 * different separator into an ISO date.
 */
export function today(at: Date = new Date()): IsoDate {
  const parts = OFFICE_DAY.formatToParts(at);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((candidate) => candidate.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

export function addDays(value: IsoDate, days: number): IsoDate {
  const date = parseIsoDate(value);
  date.setUTCDate(date.getUTCDate() + days);
  return toIsoDate(date);
}

function daysInMonth(year: number, monthIndex: number): number {
  return new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
}

/**
 * Adds calendar months, clamping to the last valid day of the target month.
 * Returns whether clamping occurred so period math can compensate.
 */
export function addMonths(value: IsoDate, months: number): { date: IsoDate; clamped: boolean } {
  const source = parseIsoDate(value);
  const day = source.getUTCDate();
  const targetMonth = source.getUTCMonth() + months;
  const targetYear = source.getUTCFullYear() + Math.floor(targetMonth / 12);
  const normalizedMonth = ((targetMonth % 12) + 12) % 12;

  const maxDay = daysInMonth(targetYear, normalizedMonth);
  const clamped = day > maxDay;
  const result = new Date(Date.UTC(targetYear, normalizedMonth, clamped ? maxDay : day));
  return { date: toIsoDate(result), clamped };
}

export function diffInDays(from: IsoDate, to: IsoDate): number {
  const ms = parseIsoDate(to).getTime() - parseIsoDate(from).getTime();
  return Math.round(ms / 86_400_000);
}

/**
 * Computes the end date of a period: `start + N months`, the SAME day of the month.
 *
 * Cover runs noon to noon — «28/03 στις 12:00 έως 28/09 στις 12:00» — so a semiannual policy
 * starting on the 28th ends on the 28th, and the next period starts on that same day. The
 * earlier `start + N months − 1 day` (28/03 → 27/09) came from a generic spec table, not from
 * the carriers' documents; renewing on it pushed every period a day later than the carrier's
 * (29/08 instead of 28/08 on ZTE8239, 24/09/2026).
 *
 * Month-end starts clamp to the last valid day (31 Jan + 1 month = 28 Feb).
 */
export function calculatePeriodEnd(start: IsoDate, frequency: RenewalFrequency): IsoDate | null {
  const months = FREQUENCY_MONTHS[frequency];
  if (months === 0) return null; // one_off has no computed end

  return addMonths(start, months).date;
}

/**
 * The next period begins the day the current one ends — at noon, when the old cover stops.
 * Dates only, so the handover day belongs to both periods.
 */
export function calculateNextPeriodStart(currentEnd: IsoDate): IsoDate {
  return currentEnd;
}

/**
 * `next_renewal_date` = end_date − lead days (spec §6.3, lead configurable).
 *
 * The lead is clamped to the period it belongs to. The default 30 days is written for
 * annual cover; applied unchanged to a monthly policy it lands the renewal date on or
 * before the period's own start — so a freshly renewed policy immediately claimed it was
 * due for renewal again, with a date in the past. Found in testing on IN-402146: period
 * 02/05 → 01/06, next renewal 02/05.
 *
 * Half the period is the cap: a renewal window that opens before the midpoint is not a
 * window, it is the whole term. `start` is optional so existing callers that only know
 * the end date keep the old behaviour.
 */
export function calculateNextRenewalDate(
  end: IsoDate,
  leadDays: number = DEFAULT_RENEWAL_LEAD_DAYS,
  start?: IsoDate,
): IsoDate {
  if (start) {
    const periodDays = diffInDays(start, end);
    if (periodDays > 0) {
      const maxLead = Math.floor(periodDays / 2);
      return addDays(end, -Math.min(leadDays, maxLead));
    }
  }
  return addDays(end, -leadDays);
}

/**
 * Resolves a full next period in one call — used by the renewal engine and by the
 * "Mark as paid" preview modal so the UI shows exactly what the server will write.
 */
export function calculateNextPeriod(
  currentEnd: IsoDate,
  frequency: RenewalFrequency,
  leadDays: number = DEFAULT_RENEWAL_LEAD_DAYS,
): { startDate: IsoDate; endDate: IsoDate; nextRenewalDate: IsoDate } | null {
  if (frequency === 'one_off') return null;
  const startDate = calculateNextPeriodStart(currentEnd);
  const endDate = calculatePeriodEnd(startDate, frequency);
  if (!endDate) return null;
  return {
    startDate,
    endDate,
    nextRenewalDate: calculateNextRenewalDate(endDate, leadDays, startDate),
  };
}

/** Days until expiry; negative once expired. */
export function daysUntil(date: IsoDate, from: IsoDate = today()): number {
  return diffInDays(from, date);
}

/**
 * Whether a policy's renewal needs an operator now.
 *
 * Two earlier versions of this were wrong, and both are worth recording because the second was
 * arrived at carefully and still missed.
 *
 * It began as `paymentStatus !== 'paid'` — "is there money to collect". That hid the action on
 * 14 policies worth 9.393,49 € which had been collected but never advanced, sitting past their
 * end dates while the server's own preview answered `willRenew: true` for every one.
 *
 * The second version added "or the period has ended". Still wrong, and for a deeper reason: it
 * kept treating payment as a signal about renewal, and in this office it is not one.
 * «Η εξόφληση γίνεται με την έκδοση / ανανέωση» — the client pays when the carrier issues, so a
 * policy inside its term is always paid, and payment therefore says nothing about whether a
 * renewal is due. Asking it made the renewals board hide its action on two thirds of the cards
 * it was listing, and left the header counting 191 «ανεξόφλητα» of which 171 were periods the
 * renewal itself had opened and declared unpaid.
 *
 * What decides it is time. `nextRenewalDate` is already end − lead days (clamped to half the
 * period), which IS the date the renewal window opens — so the question is whether that date
 * has arrived, or the period has run out.
 *
 * One-off policies are intentionally not excluded: an unpaid one still needs its payment
 * recorded, and «Εξόφληση» is how that is done. The renewal preview tells the operator that no
 * new period follows.
 */
export function isRenewalDue(
  policy: {
    status: string;
    paymentStatus: string;
    endDate: IsoDate;
    nextRenewalDate?: IsoDate | null;
  },
  from: IsoDate = today(),
): boolean {
  if (policy.status === 'cancelled') return false;
  /* First period of a policy entered unpaid: the money genuinely is outstanding. */
  if (policy.paymentStatus !== 'paid') return true;
  /* `nextRenewalDate` IS the date the renewal window opens — end − lead, clamped. */
  if (policy.nextRenewalDate && policy.nextRenewalDate <= from) return true;
  /*
   * `<= 0`, not `< 0`. Stopping at "strictly past its end date" hid the action on a policy
   * expiring TODAY — the one day it is most wanted. Nothing is cut short by acting on it:
   * `calculateNextPeriodStart` opens the next period on the end date itself (noon to noon), so
   * the closing period still covers this morning and the archive records its real dates.
   */
  return daysUntil(policy.endDate, from) <= 0;
}

/**
 * Urgency band driving the row colouring in the policy list and renewals board
 * (spec §8.4: red < 7 days, orange < 15, yellow < 30).
 */
export type UrgencyLevel = 'expired' | 'critical' | 'warning' | 'notice' | 'normal';

export function urgencyOf(endDate: IsoDate, from: IsoDate = today()): UrgencyLevel {
  const days = daysUntil(endDate, from);
  if (days < 0) return 'expired';
  if (days < 7) return 'critical';
  if (days < 15) return 'warning';
  if (days < 30) return 'notice';
  return 'normal';
}

/**
 * Months past the end date after which a lapsed policy is presumed dead.
 *
 * Not a guess about the client — a statement about the office. A policy that lapsed last
 * month is a call worth making; one that lapsed in 2011 is history, and the archive import
 * brought in 4.549 of those. Six months is where the office draws the line: past two
 * renewal notices unanswered, nobody is ringing that number again.
 */
export const NON_RENEWAL_HORIZON_MONTHS = 6;

/**
 * The oldest end date still treated as a live renewal.
 *
 * The query-side face of the same rule, so a screen that asks the server for its rows
 * (`endDateFrom`) and a screen that colours rows it already has agree on the boundary
 * instead of each carrying its own arithmetic.
 */
export function nonRenewalHorizon(from: IsoDate = today()): IsoDate {
  return addMonths(from, -NON_RENEWAL_HORIZON_MONTHS).date;
}

/**
 * Whether a lapsed policy is old enough to presume it will never be renewed.
 *
 * Deliberately NOT the same thing as `notice_status = 'declined'` («Δεν ανανεώνει»), which
 * records a client answering the call. Nobody called these; time answered for them. The two
 * must stay apart — a presumption written into the notice field would read as evidence.
 *
 * Strictly past the horizon: a policy that lapsed exactly six months ago still counts as
 * recent, which is also what makes it the inclusive `endDateFrom` bound on the query side.
 */
export function isPresumedNotRenewing(endDate: IsoDate, from: IsoDate = today()): boolean {
  return endDate < nonRenewalHorizon(from);
}

/** Generates the instalment due dates for a period split into N parts. */
export function installmentDueDates(
  periodStart: IsoDate,
  periodEnd: IsoDate,
  count: number,
): IsoDate[] {
  if (count < 1) throw new Error('Instalment count must be at least 1');
  if (count === 1) return [periodStart];

  const totalDays = diffInDays(periodStart, periodEnd);
  const step = totalDays / count;
  return Array.from({ length: count }, (_, i) => addDays(periodStart, Math.round(step * i)));
}

/** `YYYY-MM` bucket key, used by the by-month dashboard aggregations. */
export function monthKey(date: IsoDate): string {
  return date.slice(0, 7);
}

/** Inclusive list of `YYYY-MM` keys spanning a range — fills gaps in chart series. */
export function monthRange(from: IsoDate, to: IsoDate): string[] {
  const keys: string[] = [];
  let cursor = `${from.slice(0, 7)}-01`;
  const limit = `${to.slice(0, 7)}-01`;
  while (cursor <= limit) {
    keys.push(cursor.slice(0, 7));
    cursor = addMonths(cursor, 1).date;
  }
  return keys;
}

export function formatDate(value: IsoDate | null | undefined, locale = 'el-GR'): string {
  if (!value) return '—';
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(parseIsoDate(value.slice(0, 10)));
}

export function formatDateTime(value: string | null | undefined, locale = 'el-GR'): string {
  if (!value) return '—';
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}
